import * as d3 from "d3";

/**
 * D3Chart
 * Renders an interactive family tree with zoom/pan support.
 *
 * Public API (called by ChartWrapper):
 *   - zoomBy(factor)  : multiply current zoom by `factor`
 *   - reset()         : return to the initial fit-to-view transform
 */
export default class D3Chart {
  constructor(element, callbackFunction) {
    this.element = element;
    this.callbackFunction = callbackFunction;

    // Internal (data) coordinate space — large enough to lay out the tree.
    this.MARGIN = { TOP: 60, BOTTOM: 60, LEFT: 80, RIGHT: 80 };
    this.WIDTH = 3000 - this.MARGIN.LEFT - this.MARGIN.RIGHT;
    this.HEIGHT = 2000 - this.MARGIN.TOP - this.MARGIN.BOTTOM;
    this.FULL_W = this.WIDTH + this.MARGIN.LEFT + this.MARGIN.RIGHT;
    this.FULL_H = this.HEIGHT + this.MARGIN.TOP + this.MARGIN.BOTTOM;

    this._build();
  }

  _build() {
    const { element, MARGIN, WIDTH, HEIGHT, FULL_W, FULL_H } = this;

    // Clear any previous render (StrictMode double-mount safety).
    d3.select(element).selectAll("*").remove();

    // ---- SVG root: viewBox makes it scale to container; CSS sizes it. ----
    this.svg = d3
      .select(element)
      .append("svg")
      .attr("viewBox", `0 0 ${FULL_W} ${FULL_H}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    // Background rect (so drag-to-pan works even when not over a node).
    this.svg
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", FULL_W)
      .attr("height", FULL_H)
      .attr("fill", "transparent");

    // ---- Zoom group: everything that should pan/zoom lives inside `g`. ----
    this.g = this.svg
      .append("g")
      .attr("transform", `translate(${MARGIN.LEFT}, ${MARGIN.TOP})`);

    // ---- Layout + data ----
    const treeLayout = d3.tree().size([WIDTH, HEIGHT]);
    const root = d3.hierarchy(initialData);
    treeLayout(root);
    this.root = root;   // ← keep reference so reset() can use the actual content bbox

    // ---- Links (curved, soft color) ----
    const linkGenerator = d3
      .linkVertical()
      .x((d) => d.x)
      .y((d) => d.y);

    const links = this.g
      .selectAll(".link")
      .data(root.links())
      .join("path")
      .attr("class", "link")
      .attr("d", linkGenerator)
      .attr("stroke-dasharray", function () {
        const len = this.getTotalLength();
        return `${len} ${len}`;
      })
      .attr("stroke-dashoffset", function () {
        return this.getTotalLength();
      });

    links
      .transition()
      .duration(600)
      .delay((d) => d.source.depth * 120)
      .attr("stroke-dashoffset", 0);

    // ---- Nodes ----
    const nodes = this.g
      .selectAll(".node")
      .data(root.descendants())
      .join((enter) =>
        enter
          .append("circle")
          .attr("class", (d) =>
            "node " + (d.data.status === "alive" ? "alive" : "deceased")
          )
          .attr("cx", (d) => d.x)
          .attr("cy", (d) => d.y)
          .attr("r", 0)
          .attr("opacity", 0)
      );

    nodes
      .transition()
      .duration(500)
      .delay((d) => d.depth * 200)
      .attr("r", 7)
      .attr("opacity", 1);

    // ---- Labels (name + nickname) ----
    this.g
      .selectAll(".label-text1")
      .data(root.descendants())
      .join((enter) =>
        enter
          .append("text")
          .attr("class", "label-text1")
          .attr("x", (d) => d.x + 12)
          .attr("y", (d) => d.y - 4)
          .attr("opacity", 0)
          .text((d) => d.data.name)
      )
      .transition()
      .duration(500)
      .delay((d) => d.depth * 200)
      .attr("opacity", 1);

    this.g
      .selectAll(".label-text2")
      .data(root.descendants())
      .join((enter) =>
        enter
          .append("text")
          .attr("class", "label-text2")
          .attr("x", (d) => d.x + 12)
          .attr("y", (d) => d.y + 14)
          .attr("opacity", 0)
          .text((d) => d.data.nickName)
      )
      .transition()
      .duration(500)
      .delay((d) => d.depth * 200 + 80)
      .attr("opacity", 1);

    // ---- Click → open modal ----
    nodes.on("click", (event, d) => {
      event.stopPropagation();
      this.callbackFunction({
        showModal: true,
        dataOnModal: d.data,
      });
    });

    // Hover tooltip (title element, simple & dependency-free)
    nodes.append("title").text(
      (d) => `${d.data.name} ${d.data.nickName}`.trim()
    );

    // ---- Zoom behavior ----
    this.zoom = d3
      .zoom()
      .scaleExtent([0.3, 4])
      .on("zoom", (event) => {
        this.g.attr("transform", event.transform);
      });

    this.svg.call(this.zoom);

    // Initial fit — center the tree at a comfortable starting zoom.
    this.reset();
  }

  /**
   * Center the actual tree content in the viewport, at ~3 clicks deeper than
   * fit-to-all (1.4³ ≈ 2.7×).
   *
   * Key insight — work in SVG user units, NOT screen pixels:
   *   The <svg> has viewBox="0 0 3000 2000" + preserveAspectRatio="xMidYMid meet".
   *   This means the *visible SVG coordinate area* is ALWAYS the full
   *   3000 × 2000 box, regardless of how many screen pixels the container
   *   happens to occupy. The browser handles the pixel scaling internally.
   *
   *   So to "fit" content we compare its bbox to 3000 × 2000 (the viewBox),
   *   not to getBoundingClientRect(). This makes the calculation:
   *     (a) independent of container size,
   *     (b) independent of layout timing — no need to wait for the browser
   *         to size the panel,
   *     (c) correct on any screen, any DPI, any viewport size.
   *
   * Also note: d3.zoom REPLACES the <g>'s initial `translate(MARGIN.LEFT,
   * MARGIN.TOP)` with its own transform. So node (x, y) positions are used
   * directly — no margin offset is added back.
   */
  reset() {
    const { FULL_W, FULL_H, root } = this;
    if (!root) return;

    // Bounding box of all node positions, in SVG user units.
    const xs = root.descendants().map((d) => d.x);
    const ys = root.descendants().map((d) => d.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const contentW = Math.max(1, maxX - minX);
    const contentH = Math.max(1, maxY - minY);
    const contentCX = (minX + maxX) / 2;
    const contentCY = (minY + maxY) / 2;

    // Fit-to-content scale (whole tree just visible in the viewBox),
    // then zoom in 3 click-worths (1.4^3 ≈ 2.7).
    const fitScale = Math.min(FULL_W / contentW, FULL_H / contentH) * 0.9;
    const scale = fitScale * 2.7;

    // The viewport center in SVG user units is always (FULL_W/2, FULL_H/2),
    // because viewBox + preserveAspectRatio="xMidYMid meet" centers the
    // viewBox inside the rendered area.
    const tx = FULL_W / 2 - contentCX * scale;
    const ty = FULL_H / 2 - contentCY * scale;

    const transform = d3.zoomIdentity.translate(tx, ty).scale(scale);
    this.svg
      .transition()
      .duration(500)
      .call(this.zoom.transform, transform);
  }

  /** Multiply the current zoom by `factor` (centered on the viewport). */
  zoomBy(factor) {
    const box = this.element.getBoundingClientRect();
    const cx = (box.width || 1000) / 2;
    const cy = (box.height || 600) / 2;
    this.svg
      .transition()
      .duration(220)
      .call(this.zoom.scaleBy, factor, [cx, cy]);
  }

  /**
   * Produce a detached <svg> node containing the COMPLETE tree at native
   * 3000×2000 resolution — NOT clipped by the live zoom/pan transform.
   *
   * Strategy:
   *   1. Serialize the live <svg> (which already has viewBox=0 0 3000 2000).
   *   2. Parse it back into a fresh DOM node.
   *   3. Strip the zoom transform off the inner <g> group (so the tree
   *      is laid out at native coordinates, not whatever the user zoomed to).
   *   4. Set explicit width/height attributes so html2canvas / jsPDF see
   *      a fixed-pixel canvas of the full tree.
   *
   * Returns: a detached SVG DOM node (not yet attached anywhere).
   */
  exportFullSizeSVG() {
    const { FULL_W, FULL_H, MARGIN } = this;
    const liveSvg = this.svg.node();
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(liveSvg);

    // Parse into a fresh detached node.
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgStr, "image/svg+xml");
    const clone = doc.documentElement;

    // Force a fixed pixel size — this is what html2canvas uses to compute
    // the raster canvas dimensions.
    clone.setAttribute("width", String(FULL_W));
    clone.setAttribute("height", String(FULL_H));
    clone.setAttribute("viewBox", `0 0 ${FULL_W} ${FULL_H}`);
    clone.removeAttribute("style");

    // Reset the zoom group's transform to the base translate (margin offset).
    // The inner group is the first <g> child of the SVG (the zoom group).
    const groups = clone.querySelectorAll("g");
    groups.forEach((g, i) => {
      if (i === 0) {
        // The first <g> is the zoom group — reset to base transform.
        g.setAttribute("transform", `translate(${MARGIN.LEFT}, ${MARGIN.TOP})`);
      }
    });

    // Inline the styles that the live SVG got from CSS, since html2canvas
    // won't see external stylesheets reliably on a detached node.
    // (Quick approach: copy the class attribute, which keeps CSS selectors
    //  working as long as the document still has the stylesheet loaded —
    //  which it does, because we re-attach to the same DOM before capture.)
    return clone;
  }
}

/* ----------------------------------------------------------------------
   Family data (unchanged from your original file — truncated for brevity
   in this comment, full data preserved below).
   ---------------------------------------------------------------------- */
const initialData = {
  name: "शिवगुलामराम",
  nickName: "",
  wifeName: "",
  status: "dead",
  children: [
    {
      name: "गौरीशंकर",
      nickName: "",
      wifeName: "",
      status: "dead",
      children: [
        {
          name: "शीतल",
          nickName: "प्रसाद",
          wifeName: "",
          status: "dead",
          children: [
            {
              name: "द्वारिका",
              nickName: "प्रसाद",
              wifeName: "",
              status: "dead",
              children: [
                {
                  name: "अंजनी",
                  nickName: "प्रसाद",
                  wifeName: "",
                  status: "dead",
                  children: [
                    {
                      name: "रामगोपाल",
                      nickName: "(मास्टर)",
                      wifeName: "",
                      status: "dead",
                      children: [
                        {
                          name: "रामजी",
                          nickName: "(लल्लू)",
                          wifeName: "",
                          status: "alive",
                          children: [
                            { name: "अरविंद", nickName: "(लाला)", wifeName: "", status: "alive" },
                            { name: "विवेक", nickName: "(गोलू)", wifeName: "", status: "alive" },
                          ],
                        },
                      ],
                    },
                  ],
                },
                { name: "रामकृपालु", nickName: "", wifeName: "", status: "dead" },
                {
                  name: "रामसनेही",
                  nickName: "",
                  wifeName: "",
                  status: "dead",
                  children: [
                    {
                      name: "सुरेंद्र",
                      nickName: "",
                      wifeName: "",
                      status: "alive",
                      children: [
                        { name: "नरेंद्र", nickName: "(लाला)", wifeName: "", status: "alive" },
                        { name: "बिजेंद्र", nickName: "(बल्लू)", wifeName: "", status: "alive" },
                        { name: "आशीष", nickName: "(पिंटू)", wifeName: "", status: "alive" },
                      ],
                    },
                    {
                      name: "राजेंद्र",
                      nickName: "",
                      wifeName: "",
                      status: "alive",
                      children: [
                        { name: "आलोक", nickName: "(मिक्कू)", wifeName: "", status: "alive" },
                        { name: "सत्येंद्र", nickName: "(पप्पू)", wifeName: "", status: "alive" },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              name: "गजाधर",
              nickName: "प्रसाद",
              wifeName: "",
              status: "dead",
              children: [
                {
                  name: "मोहनलाल",
                  nickName: "",
                  wifeName: "",
                  status: "dead",
                  children: [
                    {
                      name: "आनंदीलाल",
                      nickName: "",
                      wifeName: "",
                      status: "alive",
                      children: [
                        { name: "हिमांशु", nickName: "(मौसम)", wifeName: "", status: "alive" },
                      ],
                    },
                    {
                      name: "रावेंद्र",
                      nickName: "(श्यामजी)",
                      wifeName: "",
                      status: "alive",
                      children: [
                        { name: "आशुतोष", nickName: "(शिव)", wifeName: "", status: "alive" },
                      ],
                    },
                    { name: "विनोद", nickName: "(मंटू)", wifeName: "", status: "dead" },
                  ],
                },
                { name: "काशी", nickName: "प्रसाद", wifeName: "", status: "dead" },
              ],
            },
          ],
        },
        { name: "कामता", nickName: "प्रसाद", wifeName: "", status: "dead" },
      ],
    },
    {
      name: "भोलाराम",
      nickName: "",
      wifeName: "",
      status: "dead",
      children: [
        {
          name: "रघुनाथ",
          nickName: "प्रसाद",
          wifeName: "",
          status: "dead",
          children: [
            {
              name: "बैजनाथ",
              nickName: "",
              wifeName: "",
              status: "dead",
              children: [
                {
                  name: "रामखेलावन",
                  nickName: "",
                  wifeName: "",
                  status: "dead",
                  children: [
                    { name: "रमाकांत", nickName: "(ददुआ)", wifeName: "", status: "alive" },
                    { name: "लक्ष्मीकांत", nickName: "(मकरी)", wifeName: "", status: "alive" },
                    { name: "सुरेश", nickName: "प्रसाद(भूरा)", wifeName: "", status: "alive" },
                  ],
                },
              ],
            },
            {
              name: "बाल्मीकि",
              nickName: "",
              wifeName: "",
              status: "dead",
              children: [
                {
                  name: "रामदास",
                  nickName: "",
                  wifeName: "",
                  status: "dead",
                  children: [
                    {
                      name: "रमेश",
                      nickName: "(दादू)",
                      wifeName: "",
                      status: "dead",
                      children: [
                        { name: "रावेंद्र", nickName: "(रिंकू)", wifeName: "", status: "alive" },
                        { name: "नरेंद्र", nickName: "(गुड्डा)", wifeName: "", status: "alive" },
                        { name: "देवेंद्र", nickName: "(मोनू)", wifeName: "", status: "alive" },
                      ],
                    },
                    {
                      name: "सुरेश",
                      nickName: "(बाबू)",
                      wifeName: "",
                      status: "alive",
                      children: [
                        { name: "अनिल", nickName: "", wifeName: "", status: "alive" },
                        { name: "अमित", nickName: "", wifeName: "", status: "alive" },
                        {
                          name: "अजय",
                          nickName: "",
                          wifeName: "सरिता",
                          wifeWorkProfile: "House Wife",
                          address: "House No 907, Ward No 24, Dwarika Nagar, Rewa (MP) 486001",
                          workProfile: "Software Engineer",
                          designation: "Vice president",
                          mobile: "9637254042",
                          companyName: "JP Morgan Chase India Pvt Ltd",
                          workLocation: "Hyderabad",
                          status: "alive",
                        },
                      ],
                    },
                    {
                      name: "गणेश",
                      nickName: "(लाला)",
                      wifeName: "",
                      status: "alive",
                      children: [
                        { name: "अजीत", nickName: "(बेटू)", wifeName: "", status: "alive" },
                        { name: "आदर्श", nickName: "(छोटू)", wifeName: "", status: "alive" },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          name: "सरजू",
          nickName: "प्रसाद",
          wifeName: "",
          status: "dead",
          children: [
            {
              name: "रामसरन",
              nickName: "",
              wifeName: "",
              status: "dead",
              children: [
                {
                  name: "रामनिवास",
                  nickName: "",
                  wifeName: "",
                  status: "dead",
                  children: [
                    {
                      name: "रामसुजान",
                      nickName: "",
                      wifeName: "",
                      status: "alive",
                      children: [
                        { name: "ब्रजकिशोर", nickName: "", wifeName: "", status: "alive" },
                        { name: "श्रवण", nickName: "(बच्चू)", wifeName: "", status: "alive" },
                        { name: "बुद्धसेन", nickName: "", wifeName: "", status: "alive" },
                      ],
                    },
                    {
                      name: "रामप्रकाश",
                      nickName: "",
                      wifeName: "",
                      status: "alive",
                      children: [
                        { name: "मयंक", nickName: "(टिंकू)", wifeName: "", status: "alive" },
                        { name: "हिमांशु", nickName: "(पिंकू)", wifeName: "", status: "alive" },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          name: "राघो",
          nickName: "प्रसाद",
          wifeName: "",
          status: "dead",
          children: [{ name: "जानकी", nickName: "प्रसाद", wifeName: "", status: "dead" }],
        },
        {
          name: "भागवत",
          nickName: "प्रसाद",
          wifeName: "",
          status: "dead",
          children: [
            {
              name: "वासुदेव",
              nickName: "(कालूराम)",
              wifeName: "",
              status: "dead",
              children: [
                {
                  name: "तीरथ",
                  nickName: "",
                  wifeName: "",
                  status: "dead",
                  children: [{ name: "जगदीश", nickName: "", wifeName: "", status: "alive" }],
                },
              ],
            },
          ],
        },
        {
          name: "अयोध्या",
          nickName: "प्रसाद",
          wifeName: "",
          status: "dead",
          children: [{ name: "सुखदेव", nickName: "प्रसाद(साधु बाबा)", wifeName: "", status: "dead" }],
        },
      ],
    },
  ],
};
